import React, { useState } from "react";
import { connect } from "react-redux";
import { Chip, Tooltip, TextField, Typography, InputAdornment } from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import dayjs from "dayjs";

import toJs from "../../../hoc/toJS";
import select from "../../../utils/select";

import KPTable from "../../../components/KPTable";
import AchievementUpdateModal from "../AchievementUpdateModal";
import AchievementDeleteModal from "../AchievementDeleteModal";
import HemisCodeConfirmModal from "../../../components/HemisCodeConfirmModal/HemisCodeConfirmModal";

import { updateAchievement, acceptAchievement } from "../../../reducer/achievement/action";
import { statisticUnaccepted } from "../../../reducer/profile/action";
import { notification } from "../../../reducer/home/action";
import { ACHIEVEMENT } from "../../../constants/selectOptions";

function AchievementTable(props) {
  const [editting, setEditting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmingAccept, setConfirmingAccept] = useState(null);

  const handleUpdate = (e) => props.setUpdating(e);
  const handleEditYear = (e) => setEditting(e);

  const handleDelete = (event, e) => {
    setDeleting(e.id);
    setAnchorEl(event.currentTarget);
  };

  const handleChange = (value) => setEditting({ ...editting, dashboardYear: value });

  const handleSubmit = (e) => {
    e.preventDefault();
    props.updateAchievement(editting.id, editting);
    setEditting(null);
  };

  const handleClear = () => {
    props.updateAchievement(editting.id, { ...editting, dashboardYear: null });
    setEditting(null);
  };

  const acceptAchievementItem = async (e) => {
    let result = await props.acceptAchievement(e.id, !e.accepted);
    return result;
  };

  const handleAccept = (e) => {
    // If already accepted (canceling confirmation), no need for modal
    if (e.accepted) {
      acceptAchievementItem(e).then((success) => {
        if (success) {
          props.statisticUnaccepted({ departmentId: 0, profileId: props.profile?.id });
          if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
        }
      });
    } else {
      // If not accepted (confirming), show modal for HemisCode
      setConfirmingAccept(e);
    }
  };

  const handleConfirmAccept = (hemisCode) => {
    const updatedData = {
      ...confirmingAccept,
      hemisCode: hemisCode,
    };

    // First update the achievement with hemisCode
    props.updateAchievement(confirmingAccept.id, updatedData).then((updateResult) => {
      // Check for ERR_HEMIS_DUPLICATE error
      if (updateResult && updateResult.error === "ERR_HEMIS_DUPLICATE") {
        props.pushNoti({
          type: "warning",
          message: `Mã CTKH "${hemisCode}" đã tồn tại trong hệ thống. Vui lòng kiểm tra lại!`,
        });
        return;
      }

      if (updateResult && updateResult.success) {
        // Then call accept API with boolean value
        props.acceptAchievement(confirmingAccept.id, !confirmingAccept.accepted).then((acceptSuccess) => {
          if (acceptSuccess) {
            props.statisticUnaccepted({ departmentId: 0, profileId: props.profile?.id });
            if (props.role === "sudo-admin") props.statisticUnaccepted({ departmentId: 0, profileId: 0 });
          }
        });
      }
    });

    setConfirmingAccept(null);
  };

  var list = [];
  if (props.type === "all") props.listAchievement.forEach((e) => list.push(e.achievement));
  else props.listAchievementById.forEach((e) => list.push(e.achievement));

  const getAwardTypeLabel = (value) => {
    const found = ACHIEVEMENT.awardType.find((e) => e.value === value);
    return found ? found.title : value || "-";
  };

  const getAwardLevelLabel = (value) => {
    const found = ACHIEVEMENT.awardLevel.find((e) => e.value === value);
    return found ? found.title : value || "-";
  };

  const columns = [
    {
      title: <b>Mã giải thưởng</b>,
      key: "awardCode",
      width: "10%",
      searchable: true,
      sorter: (a, b) => (a.awardCode || "").localeCompare(b.awardCode || ""),
    },
    {
      title: <b>Mã công trình</b>,
      key: "hemisCode",
      width: "9%",
      searchable: true,
      render: (value) =>
        value ? (
          <span style={{ wordBreak: "break-word" }}>{value}</span>
        ) : (
          <span style={{ color: "#aaa" }}>—</span>
        ),
    },
    {
      title: <b>Loại giải thưởng</b>,
      key: "awardType",
      width: "15%",
      searchable: true,
      render: (value) => getAwardTypeLabel(value),
      type: "include",
      filters: ACHIEVEMENT.awardType.map((e) => ({ value: e.value, text: e.title })),
      onFilter: (value, row) => row.filter((e) => value.includes(e.awardType)),
    },
    {
      title: <b>Cơ quan tổ chức</b>,
      key: "organizingAgency",
      width: "15%",
      searchable: true,
      sorter: (a, b) => (a.organizingAgency || "").localeCompare(b.organizingAgency || ""),
    },
    {
      title: <b>Tên đề tài/công trình</b>,
      key: "awardedProjectName",
      width: "12%",
      searchable: true,
      sorter: (a, b) => (a.awardedProjectName || "").localeCompare(b.awardedProjectName || ""),
    },
    {
      title: <b>Đơn vị được trao giải</b>,
      key: "awardedUnitName",
      width: "15%",
      searchable: true,
      sorter: (a, b) => (a.awardedUnitName || "").localeCompare(b.awardedUnitName || ""),
    },
    {
      title: <b>Mức giải thưởng</b>,
      key: "awardLevel",
      width: "10%",
      render: (value) => getAwardLevelLabel(value),
      type: "include",
      filters: ACHIEVEMENT.awardLevel.map((e) => ({ value: e.value, text: e.title })),
      onFilter: (value, row) => row.filter((e) => value.includes(e.awardLevel)),
    },
    {
      title: <b>Tác giả</b>,
      key: "achievementProfile",
      width: "15%",
      render: (value, row) => {
        if (!row?.achievementProfile || row.achievementProfile.length === 0) {
          return "-";
        }
        const authors = row.achievementProfile
          .map((member) => {
            if (member.isInternal) {
              return props.profileList.find((p) => p.id === member.profileId)?.name || "";
            } else {
              return member.externalName || "";
            }
          })
          .filter((name) => name !== "")
          .join(", ");
        return authors || "-";
      },
    },
    props.role === "sudo-admin" && props.type === "all"
      ? {
          title: <b>Năm hiển thị</b>,
          key: "dashboardYear",
          width: "10%",
          searchable: true,
          render: (value, row) =>
            row?.id !== editting?.id ? (
              <Tooltip title="Click để chỉnh sửa năm">
                <div style={{ cursor: "pointer" }} onClick={() => handleEditYear(row)}>
                  {value > 0 ? value : "Chưa có năm hiển thị"}
                </div>
              </Tooltip>
            ) : (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  size="small"
                  type="tel"
                  variant="outlined"
                  value={editting?.dashboardYear || ""}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" style={{ cursor: "pointer" }} onClick={handleClear}>
                        <Clear style={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => handleChange(parseInt(e.target.value))}
                />
              </form>
            ),
        }
      : {
          key: "dashboardYear",
          width: "0%",
          render: () => null,
        },
    {
      title: <b>Tình trạng</b>,
      key: "accepted",
      width: "12%",
      render: (value) => (
        <Chip
          size="small"
          color={value ? "primary" : "secondary"}
          label={value ? "Đã được duyệt" : "Chưa được duyệt"}
        />
      ),
      type: "include",
      filters: [
        {
          text: "Đã được duyệt",
          value: true,
        },
        {
          text: "Chưa được duyệt",
          value: false,
        },
      ],
      onFilter: (value, row) => row.filter((e) => value.includes(e.accepted)),
    },
    // {
    //   title: <b>Mục đích</b>,
    //   key: "purposeId",
    //   width: "12%",
    //   type: "include",
    //   filters: props.purposes.filter(e => e.name !== 'bonusCalculated').map((e) => ({ value: e.id, text: e.description })),
    //   onFilter: (value, row) => row.filter((e) => value.includes(e.purposeId)),
    //   render: (value) => props.purposes.filter(e => e.name !== 'bonusCalculated').find((e) => e.id === value)?.description,
    // },
    {
      title: <b>Tác vụ</b>,
      key: "action",
      width: "18%",
      render: (value, row) =>
        row?.id !== editting?.id ? (
          <>
            <Typography
              className="table-action-buton"
              variant="caption"
              color="primary"
              onClick={() => handleUpdate(row)}
            >
              Chi tiết
            </Typography>
            {row.locked ? (
              <Typography className="table-action-buton" variant="caption" color="textPrimary">
                {`Đã khóa ${
                  (row.locked === "kpi" && "KPI") ||
                  (row.locked === "reward" && "thưởng") ||
                  (row.locked === "timeResearch" && "giờ nghiên cứu")
                }`}
              </Typography>
            ) : (
              <>
                {(props.type !== "all" || props.role === "sudo-admin") && !row.accepted && (
                  <Typography
                    className="table-action-buton"
                    variant="caption"
                    color="secondary"
                    onClick={(event) => handleDelete(event, row)}
                  >
                    Xoá
                  </Typography>
                )}
                {props.role === "sudo-admin" && props.filter !== "workingPaper" && row.purposeId !== 4 && (
                  <>
                    <Typography
                      className="table-action-buton"
                      variant="caption"
                      color="primary"
                      onClick={(event) => handleAccept(row)}
                    >
                      {row.accepted ? "Hủy xác nhận" : "Xác nhận"}
                    </Typography>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Typography className="table-action-buton" variant="caption" color="primary" onClick={handleSubmit}>
              Lưu
            </Typography>
            <Typography
              className="table-action-buton"
              variant="caption"
              color="secondary"
              onClick={() => setEditting(null)}
            >
              Huỷ
            </Typography>
          </>
        ),
    },
  ];

  return (
    <>
      <KPTable
        shadow="none"
        rowsPerPage={10}
        maxHeight="calc(100vh - 288px)"
        columns={columns}
        rows={list.sort((a) => (!a.accepted ? -1 : 1))}
        loading={props.isFetching}
      />
      <AchievementUpdateModal adminTab={props.adminTab} updating={props.updating} setUpdating={props.setUpdating} />
      <AchievementDeleteModal deleting={deleting} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
      <HemisCodeConfirmModal
        open={confirmingAccept !== null}
        onClose={() => setConfirmingAccept(null)}
        onConfirm={handleConfirmAccept}
        currentHemisCode={confirmingAccept?.hemisCode || ""}
        title="Xác nhận giải thưởng"
        prefix="GT"
        dashboardYear={confirmingAccept?.dashboardYear}
        departmentId={confirmingAccept?.departmentId}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  role: select(state, "userReducer", "role"),
  profile: select(state, "profileReducer", "detail"),
  profileList: select(state, "profileReducer", "list"),
  isFetching: select(state, "achievementReducer", "isFetching"),
  listAchievement: select(state, "achievementReducer", "listFiltered"),
  listAchievementById: select(state, "achievementReducer", "listById"),
  purposes: select(state, "scientificResearchReducer", "purposes"),
  timeResearchInformationList: select(state, "timeResearchInformationReducer", "list"),
});

const mapDispatchToProps = (dispatch) => ({
  acceptAchievement: (id, accept) => dispatch(acceptAchievement(id, accept)),
  statisticUnaccepted: (payload) => dispatch(statisticUnaccepted(payload)),
  updateAchievement: (id, payload) => dispatch(updateAchievement(id, payload)),
  pushNoti: (payload) => dispatch(notification(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(toJs(AchievementTable));

